/**
 * Shared BroadcastChannel for cross-tab hosted-agent signals.
 *
 * Today this carries the delete-cleanup synchronization messages (see the
 * `cleanup-*` message types below and {@link stores/hostedAgentDeletionStore}).
 * It also owns the small sessionStorage marker that records whether the Agents /
 * agent-detail tab was opened from the workflow designer, so the detail page can
 * offer "switch back to your workflow" guidance.
 *
 * Mirrors the OAuth cross-tab pattern (see {@link services/oauthChannel}).
 */
import { sanitizeErrorMessage } from '@/utils/sanitization/errorMessage';

export const HOSTED_AGENT_CHANNEL_NAME = 'otto-hosted-agent-channel';

/**
 * sessionStorage key marking that the Agents / agent-detail tab was opened from the
 * workflow designer's "Create" / "View details" hand-off. Set on first load from the
 * `?from=designer` URL param and read by AgentDetailPage to decide whether to show the
 * "switch back to your workflow" guidance (so a direct visit to Agents doesn't show it).
 */
export const HOSTED_AGENT_FROM_DESIGNER_KEY = 'otto:hostedAgentFromDesigner';

export interface HostedAgentCleanupFailurePayload {
  agentName: string;
  message: string;
  at: number;
}

export interface HostedAgentCleanupFailedMessage {
  type: 'hosted-agent-cleanup-failed';
  key: string;
  failure: HostedAgentCleanupFailurePayload;
}

export interface HostedAgentCleanupClearedMessage {
  type: 'hosted-agent-cleanup-cleared';
  key: string;
  at: number;
}

export interface HostedAgentCleanupRecreatedMessage {
  type: 'hosted-agent-cleanup-recreated';
  key: string;
  at: number;
}

export interface HostedAgentCleanupSyncRecord {
  at: number;
  failure?: HostedAgentCleanupFailurePayload;
  recreated?: true;
}

export interface HostedAgentCleanupSnapshotRequestMessage {
  type: 'hosted-agent-cleanup-snapshot-request';
  requestId: string;
  records: Record<string, HostedAgentCleanupSyncRecord>;
}

export interface HostedAgentCleanupSnapshotMessage {
  type: 'hosted-agent-cleanup-snapshot';
  requestId: string;
  records: Record<string, HostedAgentCleanupSyncRecord>;
}

export type HostedAgentChannelMessage =
  | HostedAgentCleanupFailedMessage
  | HostedAgentCleanupClearedMessage
  | HostedAgentCleanupRecreatedMessage
  | HostedAgentCleanupSnapshotRequestMessage
  | HostedAgentCleanupSnapshotMessage;

function postHostedAgentMessage(message: HostedAgentChannelMessage): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(HOSTED_AGENT_CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
  } catch {
    // BroadcastChannel unsupported or blocked; the current tab still has local state.
  }
}

export function postHostedAgentCleanupFailed(
  key: string,
  failure: HostedAgentCleanupFailurePayload,
): void {
  postHostedAgentMessage({
    type: 'hosted-agent-cleanup-failed',
    key,
    failure: {
      ...failure,
      message: sanitizeErrorMessage(failure.message),
    },
  });
}

export function postHostedAgentCleanupCleared(key: string, at: number): void {
  postHostedAgentMessage({
    type: 'hosted-agent-cleanup-cleared',
    key,
    at,
  });
}

export function postHostedAgentCleanupRecreated(key: string, at: number): void {
  postHostedAgentMessage({
    type: 'hosted-agent-cleanup-recreated',
    key,
    at,
  });
}
